<script setup async>
import {onMounted, onBeforeUnmount, ref, computed} from "vue";
import {createError} from 'nuxt/app';
import {useHeadService} from '~/composables/useHeadService';
import {useAuthService} from '~/composables/useAuthService';
import {useMessaging} from '~/composables/useMessaging.js';
import {useMailer} from '~/composables/useMailer.js';
import FrontendHeader from "~/components/layout/FrontendHeader.vue";
import ActionMenuItem from "~/components/navigation/ActionMenuItem.vue";
import ShortLinkShareWrapper from "~/components/share/ShortLinkShareWrapper.vue";
import VisitorPreviewBanner from "~/components/VisitorPreviewBanner.vue";
import {clubFacts} from "~/utils/privateAccess";
import AccessApplicationCell from "~/components/users/AccessApplicationCell.vue";
import JoinRequestPending from "~/components/join/JoinRequestPending.vue";
import NoResults from "~/components/loaders/NoResults.vue";
import FixtureCell from "~/components/fixtures/FixtureCell.vue";
import LeagueClickableCell from "~/components/lists/LeagueClickableCell.vue";
import LadderClickableCell from "~/components/lists/LadderClickableCell.vue";
import MeetClickableCell from "~/components/lists/MeetClickableCell.vue";
import AmericanoClickableCell from "~/components/lists/AmericanoClickableCell.vue";
import PlayerList from "~/components/players/PlayerList.vue";
import TournamentClickableCell from "~/components/lists/TournamentClickableCell.vue";
import AvatarImageLoader from "~/components/loaders/AvatarImageLoader.vue";
import FixtureResultRow from "~/components/fixtures/FixtureResultRow.vue";
import {useRuntimeConfig} from "nuxt/app";
import ArchivedNotice from "~/components/ArchivedNotice.vue";
import JoinSettingsCard from "~/components/join/JoinSettingsCard.vue";
import PlayerCard from "~/components/players/PlayerCard.vue";

definePageMeta({
  name: 'ClubDetail',
  path: '/clubs/:clubId',
});
const {t} = useI18n()
const route = useRoute();
const clubId = route.params.clubId;

const {item: page, getItem: getPageRaw, hasItemErrors: hasPageErrors} = useCrudApi("api/v2/page/club");
// ?viewAs=visitor (the admin dashboard's "Public page" tile): the API gates the
// page as if nobody were signed in, so an admin sees what a visitor sees.
const visitorPreview = computed(() => route.query.viewAs === 'visitor');
const getPage = (id) => getPageRaw(id, visitorPreview.value ? {'X-View-As': 'visitor'} : {});
await getPage(route.params.clubId);
// "Back to my view" only drops ?viewAs from the address: same page, same
// component, so nothing reloaded and the visitor layout stayed. Ask again.
watch(visitorPreview, () => getPage(route.params.clubId));
if (hasPageErrors.value) {
  throw createError({
    statusCode: 404, // or another relevant status code
    message: 'Club not found'
  });
}

const {setMeta} = useHeadService();
setMeta(
    page.value?.club?.name + ' · Club' + (page.value?.club?.hasFixedAddress ? ' based in ' + page.value?.club?.city : '') + ' on Tap Leagues',
    page.value?.club?.name + ' is a club' + (page.value?.club?.hasFixedAddress ? ' based in ' + page.value?.club?.city : '') + ' on Tap Leagues. Find out more about ' + page.value?.club?.name + ' and request to join through Tap Leagues.'
);
const {isAdminOfClub, isMemberOfClub, isAuthenticated, user} = useAuthService();
const router = useRouter();
const {filtersToQuery} = useMailer();
const {leaveClub, leavePending} = useLeaveEntity();
// In the visitor preview the admin is shown the page as an outsider: no admin or
// member controls, the visitor's join banner and the visitor's member list.
const clubAdmin = computed(() => !visitorPreview.value && isAdminOfClub(page.value?.club?.id));
const clubMember = computed(() => !visitorPreview.value && isMemberOfClub(page.value?.club?.id));

// The club has been deleted (archived) - show the deleted notice instead of content.
const clubArchived = computed(() => !!page.value?.club?.archivedAt);
const clubPrivate = computed(() => !!page.value?.privateToViewer);
usePrivatePage(clubPrivate);

// Filter join link through the same visibility rules as the other competition pages
const visibleJoinLink = computed(() => {
  const raw = page.value?.club?.visibleInviteLink;
  if (!raw) return null;
  if (clubAdmin.value) return raw;

  // Non-admins require: visible (0), active, and scope-allowed. The visitor preview
  // still gets the admin's copy of the link, so it also drops one shown to members only (2),
  // which the API never sends a real visitor.
  const scopeAllowed = raw.inviteScope !== 1 || clubMember.value;
  const pageAllowed = visitorPreview.value ? raw.inviteEntityPageSetting === 0 : raw.inviteEntityPageSetting !== 1;
  return pageAllowed && raw.isActive && scopeAllowed
      ? raw : null;
});

// Does the visible join link auto-join, or require admin approval?
const autoJoin = computed(() => visibleJoinLink.value?.inviteJoinSetting === 0);

// The user's own pending join request for this club (from the auth user object).
const {isPending: hasPendingRequest, cancelling: cancelPending, cancelRequest} =
    useJoinRequestStatus('club', computed(() => page.value?.club?.id));

// Static map for the location card (Google Static Maps; tapping opens directions).
const config = useRuntimeConfig();
const mapsApiKey = config.public?.grantThomasNuxt?.googleMapsApiKey;
const clubLocation = computed(() => page.value?.club?.location);
const staticMapUrl = computed(() => {
  const loc = clubLocation.value;
  if (!loc || !mapsApiKey) return null;
  return `https://maps.googleapis.com/maps/api/staticmap?center=${loc.latitude},${loc.longitude}`
      + `&zoom=15&size=640x300&scale=2&markers=color:0xE2231A%7C${loc.latitude},${loc.longitude}&key=${mapsApiKey}`;
});
const directionsUrl = computed(() => {
  const loc = clubLocation.value;
  if (!loc) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${loc.latitude},${loc.longitude}`;
});

// Members list shows everyone, filterable by a keyword search.
const memberSearch = ref('');
const filteredMembers = computed(() => {
  const q = memberSearch.value.trim().toLowerCase();
  const all = page.value?.players || [];
  if (!q) return all;
  return all.filter(p => (p.fullName || '').toLowerCase().includes(q));
});

// Messaging and mailer composables
const {entityChatLoading, createOrOpenEntityChat} = useMessaging();

// Chat handler function
const handleChatClick = async () => {
  await createOrOpenEntityChat(
      {clubId: page.value.club.id, conversationId: page.value.club.conversationId},
      () => getPage(route.params.clubId)
  );
};

// Handler for leaving the club
const handleLeaveClub = async () => {
  try {
    await leaveClub(page.value.club.id);
    showLeaveClubDialog.value = false;
    // Refresh page to reflect changes
    await getPage(route.params.clubId);
  } catch (error) {
    console.error('Failed to leave club:', error);
    // Keep dialog open on error so user can see the error message
  }
};

const showLeaveClubDialog = ref(false);

// Top-right keeps only the admin action; everything else lives in the action bar.
const titleActions = computed(() => {
  const actions = [];
  if (clubAdmin.value) {
    actions.push({
      title: 'Edit club',
      icon: 'mdi-pencil',
      click: () => router.push({name: 'ClubDashboard', params: {clubId: page.value.club.id}}),
      color: 'info',
    });
  }
  return actions;
});
const {addListener, removeLocalListenersWithEvent} = useListenerService();
onMounted(() => {
  addListener("requestsUpdated", () => getPage(route.params.clubId));
});
onBeforeUnmount(() => {
  removeLocalListenersWithEvent("requestsUpdated");
});
</script>

<template>
  <base-page>
    <!-- Deleted (archived) club - show the notice instead of any content -->
    <!-- Members only for this viewer (PRIVACY_PLAN.md §4.1): the stub keeps the name and the join link -->
    <v-container v-if="clubPrivate" class="large">
      <visitor-preview-banner v-if="visitorPreview"/>
      <frontend-header
          :club="page.club"
          :image-file-name="page.club.imageFileName"
          :title="page.club.name"
          title-icon="mdi-account-group"
      >
        <template #subTitle>
          <div class="club-header-sub">
            <span class="club-header-sub-text">
              {{ page.club.playerCount }} members<template v-if="page.club.city"> · {{ page.club.city }}</template>
            </span>
          </div>
        </template>
      </frontend-header>
      <div class="tl-action-strip tl-action-strip--solo my-5">
        <short-link-share-wrapper>
          <template #default="{ click, loading: shareLoading }">
            <button class="as" @click="click" :disabled="shareLoading">
              <v-icon size="17" icon="mdi-share-variant"/>Share
            </button>
          </template>
        </short-link-share-wrapper>
      </div>
      <v-row align="start">
        <v-col cols="12" :md="page.club.hasFixedAddress ? 8 : 12">
          <private-notice reason="club" entity="club" :name="page.club.name" :join-slug="visibleJoinLink?.slug"
                          :unlisted="page.club.listedInSearch === false"
                          :facts="clubFacts(page.summary, page.club)" facts-title="What goes on here" :preview="visitorPreview"/>
        </v-col>
        <v-col v-if="page.club.hasFixedAddress" cols="12" md="4">
          <div class="tl-card club-location-card">
            <a v-if="staticMapUrl" :href="directionsUrl" target="_blank" rel="noopener" class="club-map">
              <img :src="staticMapUrl" :alt="'Map of ' + page.club.name" class="club-map-img"/>
            </a>
            <a :href="directionsUrl || '#'" target="_blank" rel="noopener" class="club-location-body">
              <div class="club-location-info">
                <div class="club-location-line1">{{ page.club.addressLine1 }}</div>
                <div v-if="page.club.city || page.club.postCode" class="club-location-line2">
                  {{ [page.club.city, page.club.postCode].filter(Boolean).join(' ') }}
                </div>
              </div>
              <v-icon size="20" color="primary">mdi-directions</v-icon>
            </a>
          </div>
        </v-col>
      </v-row>
    </v-container>
    <v-container v-else-if="clubArchived" class="large">
      <archived-notice entity="club" :name="page.club.name"/>
    </v-container>
    <v-container v-else class="large">
      <visitor-preview-banner v-if="visitorPreview"/>

      <!-- Leave Club Confirmation Dialog -->
      <v-dialog v-model="showLeaveClubDialog" max-width="400">
        <div class="tl-confirm">
          <h3>Leave {{ page.club.name }}?</h3>
          <p>You'll be removed from the club and will need to request access again to rejoin.</p>
          <div class="tl-confirm-actions">
            <v-btn variant="text" size="large" @click="showLeaveClubDialog = false">Cancel</v-btn>
            <v-btn color="error" variant="flat" size="large" :loading="leavePending" @click="handleLeaveClub">
              Leave club
            </v-btn>
          </div>
        </div>
      </v-dialog>

      <!-- Header (consistent with ladder / tournament) -->
      <frontend-header
          :club="page.club"
          :image-file-name="page.club.imageFileName"
          :title="page.club.name"
          title-icon="mdi-account-group"
          :actions="titleActions"
      >
        <template #subTitle>
          <div class="club-header-sub">
            <span class="club-header-sub-text">
              {{ page.club.playerCount }} members<template v-if="page.club.city"> · {{ page.club.city }}</template>
            </span>
            <span v-if="clubMember" class="tl-chip tl-chip--brand">Member</span>
            <span v-else-if="clubAdmin" class="tl-chip tl-chip--brand">Admin</span>
          </div>
        </template>
      </frontend-header>

      <!-- Action bar (shared .tl-action-strip — same as ladder / tournament) -->
      <div class="tl-action-strip my-5">
        <!-- Share -->
        <short-link-share-wrapper
                    :join-reference-type="0" :join-reference-id="page?.club?.id" :can-share-join="clubAdmin">
          <template #default="{ click, loading: shareLoading }">
            <button class="as" @click="click" :disabled="shareLoading">
              <v-icon size="17" icon="mdi-share-variant"/>Share
            </button>
          </template>
        </short-link-share-wrapper>

        <!-- Chat (member) -->
        <button v-if="isAuthenticated && clubMember" class="as" @click="handleChatClick" :disabled="entityChatLoading">
          <v-icon size="17" icon="mdi-forum"/>{{ page.club.conversationId ? 'Open Chat' : 'Start Chat' }}
        </button>

        <!-- Stats -->
        <router-link class="as" :to="{ name: 'ClubStats', params: { clubId: page.club.id } }">
          <v-icon size="17" icon="mdi-chart-line"/>Stats
        </router-link>

        <!-- Fixtures -->
        <router-link class="as" :to="{ name: 'FixtureSearch', query: { clubIds: [page.club.id] } }">
          <v-icon size="17" icon="mdi-scoreboard"/>Matches
        </router-link>

        <!-- Admin: Email -->
        <template v-if="clubAdmin">
          <div class="vsep"></div>
          <router-link
              class="as"
              :to="{ ...route, query: { ...route.query, mailerQuery: filtersToQuery({ clubIds: [page.club.id] }) } }"
          >
            <v-icon size="17" icon="mdi-email"/>Email
          </router-link>
        </template>

        <!-- More — Leave tucked away (member) -->
        <v-menu v-if="clubMember" location="bottom end">
          <template #activator="{ props }">
            <button class="as" v-bind="props"><v-icon size="17" icon="mdi-dots-horizontal"/>More</button>
          </template>
          <v-list density="compact">
            <v-list-item @click="showLeaveClubDialog = true">
              <template #prepend><v-icon size="18" color="error">mdi-exit-to-app</v-icon></template>
              <v-list-item-title class="text-error">Leave club</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>

      <!-- Admin invite CTA — join-link state at a glance, opens the join-links dialog -->
      <join-settings-card
          v-if="clubAdmin"
          class="mb-5"
          :reference-type="0"
          :reference-id="page.club.id"
          :club-id="page.club.id"
          :entity-name="page.club.name"
          @changed="getPage(route.params.clubId)"
      />

      <!-- Pending request — already applied, awaiting approval -->
      <join-request-pending
          v-if="!clubMember && !clubAdmin && !visitorPreview && hasPendingRequest"
          :entity-name="page.club.name"
          :cancelling="cancelPending"
          @cancel="cancelRequest"
      />

      <!-- Join banner — non-members / guests with no pending request (mirrors the Flutter join block) -->
      <div v-else-if="!clubMember && !clubAdmin && visibleJoinLink" class="tl-card club-join-banner mb-5">
        <div class="club-join-ic"><v-icon color="primary">mdi-account-plus</v-icon></div>
        <div class="flex-fill">
          <div class="club-join-title">This club is open to join</div>
          <div class="club-join-sub">{{ autoJoin ? 'Join instantly' : 'An admin approves requests to join' }}</div>
        </div>
        <v-btn color="primary" rounded="pill" :to="'/invite/' + visibleJoinLink.slug" prepend-icon="mdi-account-plus"
               :disabled="visitorPreview">Join</v-btn>
      </div>

      <!-- Pending applications alert (admin only) -->
      <template v-if="!visitorPreview && page.applications.length > 0">
        <div class="tl-section-head mt-0"><h2>Requiring your attention</h2></div>
        <div class="tl-card mb-4">
          <access-application-cell
              :request="application"
              v-for="application in page.applications"
              :key="application.id"
          ></access-application-cell>
        </div>
      </template>

      <!-- Two-column layout -->
      <v-row align="start">

        <!-- ========== LEFT COLUMN ========== -->
        <v-col cols="12" md="8">

          <!-- Competitions -->
          <template v-if="page.leagues.length > 0 || (page.americanos ?? []).length > 0 || page.ladders.length > 0 || page.tournaments.length > 0 || (page.meets ?? []).length > 0">
            <div class="tl-section-head">
              <h2>Competitions</h2>
            </div>
            <div class="tl-card club-comp-list">
              <league-clickable-cell
                  v-for="league in page.leagues"
                  :key="league.id"
                  :league="league"
                  class="club-comp-item"
              />
              <americano-clickable-cell
                  v-for="americano in page.americanos ?? []"
                  :key="americano.id"
                  :americano="americano"
                  class="club-comp-item"
              />
              <ladder-clickable-cell
                  v-for="ladder in page.ladders"
                  :key="ladder.id"
                  :ladder="ladder"
                  class="club-comp-item"
              />
              <tournament-clickable-cell
                  v-for="tournament in page.tournaments"
                  :key="tournament.id"
                  :tournament="tournament"
                  class="club-comp-item"
              />
              <meet-clickable-cell
                  v-for="meet in page.meets ?? []"
                  :key="meet.id"
                  :meet="meet"
                  class="club-comp-item"
              />
            </div>
          </template>

          <!-- Members — full list, keyword-searchable -->
          <div class="tl-section-head club-members-head">
            <h2>Members</h2>
            <v-text-field
                v-model="memberSearch"
                placeholder="Search members"
                prepend-inner-icon="mdi-magnify"
                density="compact"
                hide-details
                clearable
                class="club-member-search"
            />
          </div>
          <no-results v-if="page.players.length === 0">
            <template v-if="clubMember">No members in this club yet</template>
            <template v-else>Only members with a public profile are shown to people outside the club.</template>
          </no-results>
          <no-results v-else-if="filteredMembers.length === 0">
            No members match "{{ memberSearch }}"
          </no-results>
          <div v-else class="tl-card club-members-list">
            <!-- Standard player card: consistent avatar/name + the expandable
                 player dialog (message, profile) instead of a bare link. -->
            <div
                v-for="player in filteredMembers"
                :key="player.id"
                class="club-member-row"
            >
              <player-card :player="player" class="club-member-name-col"/>
              <span v-if="page.club.adminPlayerIds && page.club.adminPlayerIds.includes(player.id)"
                    class="tl-chip">Admin</span>
              <span v-else-if="user && player.id === user.playerId" class="tl-chip tl-chip--brand">You</span>
            </div>
          </div>

        </v-col>

        <!-- ========== RIGHT COLUMN (sticky) ========== -->
        <v-col cols="12" md="4">
          <div class="club-side-sticky">

            <!-- Location / map card -->
            <div v-if="page.club.hasFixedAddress" class="tl-card club-location-card">
              <a v-if="staticMapUrl" :href="directionsUrl" target="_blank" rel="noopener" class="club-map">
                <img :src="staticMapUrl" :alt="'Map of ' + page.club.name" class="club-map-img"/>
              </a>
              <a :href="directionsUrl || '#'" target="_blank" rel="noopener" class="club-location-body">
                <div class="club-location-info">
                  <div class="club-location-line1">{{ page.club.addressLine1 }}</div>
                  <div v-if="page.club.city || page.club.postCode" class="club-location-line2">
                    {{ [page.club.city, page.club.postCode].filter(Boolean).join(' ') }}
                  </div>
                </div>
                <v-icon color="primary" size="20">mdi-directions</v-icon>
              </a>
            </div>

            <!-- Stats -->
            <div class="tl-section-head">
              <h2>Club stats</h2>
              <router-link :to="{ name: 'ClubStats', params: { clubId: page.club.id } }">Full stats</router-link>
            </div>
            <div class="club-stat-grid">
              <div class="club-stat-card">
                <div class="club-stat-v">{{ page.fixtureCount }}</div>
                <div class="club-stat-k">Matches played</div>
              </div>
              <div class="club-stat-card">
                <div class="club-stat-v">{{ page.leagues.length + (page.americanos ?? []).length + page.ladders.length + page.tournaments.length + (page.meets ?? []).length }}</div>
                <div class="club-stat-k">Competitions</div>
              </div>
            </div>

            <!-- Recent results -->
            <div class="tl-section-head">
              <h2>Recent results</h2>
              <a href="#"
                 @click.prevent="router.push({ name: 'FixtureSearch', query: { clubIds: [page.club.id] } })">
                See all
              </a>
            </div>
            <crud-list-loader
                path="api/v2/fixtures"
                :listeners="['fixtureUpdated']"
                :per-page="3"
                :custom-filters="{ clubIds: [page.club.id], isPlayed : true }"
            >
              <template #loader>
                <div class="tl-card">
                  <v-skeleton-loader type="list-item-two-line" />
                  <v-skeleton-loader type="list-item-two-line" />
                  <v-skeleton-loader type="list-item-two-line" />
                </div>
              </template>
              <template #empty>
                <no-results>{{ t("club_no_fixtures") }}</no-results>
              </template>
              <template #default="{ items: latestFixtures }">
                <no-results v-if="latestFixtures.length === 0">{{ t("club_no_fixtures") }}</no-results>
                <div v-else class="club-results-list">
                  <fixture-result-row
                      v-for="fixture in latestFixtures"
                      :key="fixture.id"
                      :fixture="fixture"
                  />
                </div>
              </template>
            </crud-list-loader>

          </div>
        </v-col>

      </v-row>

    </v-container>
  </base-page>
</template>

<style scoped lang="scss">
/* ---- Header subtitle (members · city + membership chip) ---- */
.club-header-sub {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 4px;
}

.club-header-sub-text {
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
}

/* ---- Join banner (non-members) ---- */
.club-join-banner {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
}

.club-join-ic {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: rgb(var(--v-theme-brandTint));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.club-join-title {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.2px;
}

.club-join-sub {
  font-size: 11.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 1px;
}

/* ---- Location map ---- */
.club-map {
  display: block;
  line-height: 0;
}

.club-map-img {
  width: 100%;
  height: 150px;
  object-fit: cover;
  display: block;
}

/* sticky side column (the v-col handles the grid; this just pins its content) */
.club-side-sticky {
  position: sticky;
  top: 84px; /* topbar height + padding */
}

/* ---- Competitions ---- */
/* Override ClickableCell's own .tl-card + margin so items run flush inside our container */
.club-comp-list {
  padding: 2px 0;

  /* strip each competition cell's own card chrome — our parent card provides the shell */
  :deep(.comp-row) {
    margin-bottom: 0;
    border-radius: 0 !important;
    border: none !important;
    border-bottom: 1px solid rgb(var(--v-theme-borderLight)) !important;
    overflow: visible;

    &:last-child {
      border-bottom: none !important;
    }
  }

  /* the wrapper component adds mb-3 — zero it out */
  :deep(> *) {
    margin-bottom: 0 !important;
  }
}

/* ---- Members ---- */
/* search box sits opposite the heading (override the section-head baseline) */
.club-members-head {
  align-items: center;
  gap: 12px;
}

.club-member-search {
  flex: 0 1 220px;
  max-width: 220px;
}

/* pill shape to match the members-toolbar search (override the global 12px field radius) */
.club-member-search :deep(.v-field) {
  border-radius: 999px !important;
}

.club-member-search :deep(.v-field__outline__start) {
  border-radius: 999px 0 0 999px !important;
}

.club-member-search :deep(.v-field__outline__end) {
  border-radius: 0 999px 999px 0 !important;
}

/* full list scrolls within a fixed height rather than pushing the page down */
.club-members-list {
  max-height: 420px;
  overflow-y: auto;
}

.club-member-row {
  display: flex;
  align-items: center;
  padding: 11px 16px;
  text-decoration: none;
  color: rgb(var(--v-theme-text));

  & + & {
    border-top: 1px solid rgb(var(--v-theme-borderLight));
  }

  &:hover {
    background: rgb(var(--v-theme-light));
  }
}

.club-member-name-col {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.club-member-face {
  width: 32px !important;
  height: 32px !important;
  border-radius: 50% !important;
  flex-shrink: 0;
}

.club-member-face--fallback {
  background: rgb(var(--v-theme-light));
  border: 1px solid rgb(var(--v-theme-borderLight));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10.5px;
  font-weight: 700;
  color: rgb(var(--v-theme-charcoal));
}

.club-member-nm {
  font-size: 13px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ---- Location card ---- */
.club-location-card {
  overflow: hidden;
}

.club-location-body {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  text-decoration: none;
  color: inherit;
  border-top: 1px solid rgb(var(--v-theme-borderLight));

  &:hover { background: rgb(var(--v-theme-light)); }
}

.club-location-info {
  flex: 1;
  min-width: 0;
}

.club-location-line1 {
  font-size: 13px;
  font-weight: 600;
}

.club-location-line2 {
  font-size: 11.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 1px;
}

/* ---- Stats grid ---- */
.club-stat-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.club-stat-card {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 14px;
  padding: 14px 16px;
}

.club-stat-v {
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.5px;
}

.club-stat-k {
  font-size: 10.5px;
  font-weight: 700;
  color: rgb(var(--v-theme-textSubtle));
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-top: 2px;
}

/* ---- Recent results ---- */
.club-results-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
</style>
